import { WxStationFeatureProps, WxStationHistoryPoint } from '../../types';
import store from '../../state';
import { appendAuthToken, authenticatedFetch, getApiServer } from '../../config';
import { ForecastBinaryService, DecodedPointForecast } from '../../services/ForecastBinaryService';
import TierAccessService, { TileAccessDenial } from '../../services/TierAccessService';
import { PoiDataService, getElevationAt } from '../../services/PoiDataService';
import { poiModal } from './PoiModal';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export type HistoryChartTab = 'temp' | 'precip' | 'wind' | 'rh' | 'pressure';
type HistoryHoursWindow = 24 | 168 | 336;

interface CachedHistoryEntry {
  fetchedAt: number;
  source: string;
  observations: WxStationHistoryPoint[];
}

interface SampledForecastStep {
  t: number;
  temp_c: number;
  temp_f: number;
  dew_c: number;
  dew_f: number;
  rh: number;
  wspd_mph: number;
  wgst_mph: number | null;
  pcp_in: number;
  pcp_mm: number;
}

export class WxStationModal {
  private dialog: HTMLDialogElement;
  private chart: Chart | null = null;
  private currentStation: WxStationFeatureProps | null = null;
  private activeWindowHours: HistoryHoursWindow = 24;
  private activeChartTab: HistoryChartTab = 'temp';
  private rawSeries: WxStationHistoryPoint[] = [];
  private activeSourceLabel: string = 'NOAA AWC';
  private isLoading: boolean = false;
  private loadError: string | null = null;
  private accessDenial: TileAccessDenial | null = null;
  private fetchToken: number = 0;
  private historyCache: Map<string, CachedHistoryEntry> = new Map();
  private forecastExpanded: boolean = false;
  private isForecastLoading: boolean = false;
  private stationForecast: DecodedPointForecast | null = null;
  private forecastModelLabel: string = 'NOAA NBM';
  private forecastCache: Map<string, { forecast: DecodedPointForecast; label: string }> = new Map();

  constructor() {
    this.injectStyles();

    this.dialog = document.createElement('dialog');
    this.dialog.id = 'wx-station-dialog';
    this.dialog.className = 'wx-station-dialog';
    this.dialog.setAttribute('closedby', 'any');
    this.dialog.setAttribute('aria-labelledby', 'wx-station-modal-title');

    this.dialog.addEventListener('close', () => {
      this.dialog.returnValue = '';
      this.destroyChart();
    });

    // Progressive enhancement fallback for browsers lacking <dialog closedby="any">
    if (typeof HTMLDialogElement !== 'undefined' && !('closedBy' in HTMLDialogElement.prototype)) {
      const isOutside = (e: PointerEvent, dlg: HTMLDialogElement): boolean => {
        const r = dlg.getBoundingClientRect();
        return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
      };
      let pressedOutside = false;
      this.dialog.addEventListener('pointerdown', (e: PointerEvent) => {
        pressedOutside = isOutside(e, this.dialog);
      });
      this.dialog.addEventListener('pointerup', (e: PointerEvent) => {
        if (pressedOutside && isOutside(e, this.dialog)) {
          this.dialog.close();
        }
        pressedOutside = false;
      });
    }

    if (typeof document !== 'undefined' && document.body) {
      document.body.appendChild(this.dialog);
    }

    let lastTier = TierAccessService.getUserAccessTier(store.getState());
    let lastToken = store.getState().sessionToken;
    store.subscribe((newState) => {
      const nextTier = TierAccessService.getUserAccessTier(newState);
      if (nextTier !== lastTier || newState.sessionToken !== lastToken) {
        lastTier = nextTier;
        lastToken = newState.sessionToken;
        if (this.dialog.open && this.currentStation) {
          this.accessDenial = null;
          this.isLoading = true;
          void this.loadHistory(this.currentStation.id);
        }
      }
    });
  }

  private injectStyles(): void {
    if (typeof document === 'undefined' || document.getElementById('wx-station-dialog-styles')) {
      return;
    }
    const style = document.createElement('style');
    style.id = 'wx-station-dialog-styles';
    style.textContent = `
      dialog.wx-station-dialog {
        border: 1px solid rgba(71, 85, 105, 0.65);
        border-radius: 14px;
        padding: 0;
        width: min(94vw, 880px);
        max-height: 90vh;
        background: #0f172a;
        color: #f1f5f9;
        box-shadow: 0 24px 54px rgba(0, 0, 0, 0.75);
        font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      }
      dialog.wx-station-dialog[open] {
        display: flex;
        flex-direction: column;
      }
      dialog.wx-station-dialog:modal {
        overscroll-behavior: contain;
      }
      dialog.wx-station-dialog::backdrop {
        background-color: rgba(2, 6, 23, 0.72);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        overflow: hidden;
        overscroll-behavior: contain;
      }
      .wx-modal-scroll {
        padding: 20px 22px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 14px;
      }
      .wx-modal-pill-group {
        display: inline-flex;
        gap: 3px;
        background: #1e293b;
        padding: 3px;
        border-radius: 7px;
        border: 1px solid rgba(71, 85, 105, 0.55);
      }
      .wx-modal-pill-btn {
        padding: 5px 10px;
        font-size: 11.5px;
        font-weight: 700;
        border-radius: 5px;
        border: none;
        background: transparent;
        color: #94a3b8;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .wx-modal-pill-btn.active {
        background: #38bdf8;
        color: #020617;
      }
      .wx-modal-metric-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(142px, 1fr));
        gap: 10px;
      }
      .wx-modal-metric-card {
        background: rgba(30, 41, 59, 0.75);
        border: 1px solid rgba(71, 85, 105, 0.45);
        border-radius: 9px;
        padding: 10px 12px;
        display: flex;
        flex-direction: column;
        gap: 3px;
      }
      .wx-forecast-expand-btn {
        position: absolute;
        z-index: 6;
        padding: 6px 11px;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.2px;
        border-radius: 999px;
        border: 1px solid rgba(56, 189, 248, 0.75);
        background: linear-gradient(135deg, rgba(15, 23, 42, 0.94) 0%, rgba(30, 41, 59, 0.96) 100%);
        color: #38bdf8;
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.65), 0 0 12px rgba(56, 189, 248, 0.22);
        cursor: pointer;
        white-space: nowrap;
        transform: translate(-50%, -50%);
        transition: transform 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;
      }
      .wx-forecast-expand-btn:hover:not(:disabled) {
        background: #38bdf8;
        color: #020617;
        box-shadow: 0 8px 22px rgba(56, 189, 248, 0.45);
        transform: translate(-50%, -50%) scale(1.04);
      }
      .wx-forecast-expand-btn:disabled {
        opacity: 0.75;
        cursor: wait;
      }
      .wx-forecast-collapse-btn {
        position: absolute;
        top: 4px;
        right: 8px;
        z-index: 6;
        padding: 3px 9px;
        font-size: 10.5px;
        font-weight: 700;
        border-radius: 999px;
        border: 1px solid rgba(56, 189, 248, 0.45);
        background: rgba(15, 23, 42, 0.88);
        color: #7dd3fc;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .wx-forecast-collapse-btn:hover {
        background: rgba(56, 189, 248, 0.22);
        color: #f8fafc;
      }
      .wx-full-forecast-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        padding: 8px 15px;
        font-size: 0.78rem;
        font-weight: 800;
        border-radius: 8px;
        border: 1px solid rgba(56, 189, 248, 0.55);
        background: linear-gradient(135deg, #0284c7 0%, #2563eb 100%);
        color: #f8fafc;
        cursor: pointer;
        box-shadow: 0 4px 14px rgba(2, 132, 199, 0.35);
        transition: filter 0.15s ease, transform 0.12s ease;
      }
      .wx-full-forecast-btn:hover {
        filter: brightness(1.1);
        transform: translateY(-1px);
      }
      @media (max-width: 600px) {
        .wx-modal-scroll {
          padding: 15px 14px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  public open(station: WxStationFeatureProps, initialMetricTab?: HistoryChartTab): void {
    this.currentStation = station;
    if (initialMetricTab) {
      this.activeChartTab = initialMetricTab === 'rh' ? 'temp' : initialMetricTab;
    } else if (this.activeChartTab === 'rh') {
      this.activeChartTab = 'temp';
    }
    this.rawSeries = [];
    this.loadError = null;
    this.accessDenial = null;
    this.isLoading = true;
    this.forecastExpanded = false;
    this.isForecastLoading = false;
    this.stationForecast = null;
    this.forecastModelLabel = 'NOAA NBM';

    this.render();
    if (!this.dialog.open) {
      this.dialog.showModal();
    }
    void this.loadHistory(station.id);
  }

  public close(): void {
    if (this.dialog.open) {
      this.dialog.close();
    }
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private static degToCardinal(deg: number | null | undefined): string {
    if (deg === null || deg === undefined || !Number.isFinite(deg)) return '';
    const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    return dirs[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16];
  }

  private static flightCatStyle(cat: string | null | undefined): { bg: string; fg: string; label: string } {
    const c = (cat || '').toUpperCase();
    if (c === 'VFR') return { bg: 'rgba(34, 197, 94, 0.2)', fg: '#4ade80', label: 'VFR • Visual' };
    if (c === 'MVFR') return { bg: 'rgba(59, 130, 246, 0.22)', fg: '#60a5fa', label: 'MVFR • Marginal' };
    if (c === 'IFR') return { bg: 'rgba(239, 68, 68, 0.22)', fg: '#f87171', label: 'IFR • Instrument' };
    if (c === 'LIFR') return { bg: 'rgba(217, 70, 239, 0.22)', fg: '#e879f9', label: 'LIFR • Low IFR' };
    return { bg: 'rgba(148, 163, 184, 0.15)', fg: '#cbd5e1', label: c || 'Surface Obs' };
  }

  private static formatObsTimestamp(iso?: string): string {
    if (!iso) return 'Recent';
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms)) return iso;
    const mins = Math.max(0, Math.round((Date.now() - ms) / 60000));
    const ageStr = mins < 1 ? 'Just now' : (mins < 60 ? `${mins}m ago` : `${(mins / 60).toFixed(1)}h ago`);
    const localStr = new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `${ageStr} (${localStr})`;
  }

  private static escapeHtml(raw: string): string {
    return raw
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private static parseClientMetarRemarksPrecip(raw?: string, obsEpoch?: number): {
    pcp1hIn: number | null;
    pcp3hIn: number | null;
    pcp6hIn: number | null;
    pcp24hIn: number | null;
    metarType: 'METAR' | 'SPECI' | null;
  } {
    if (!raw) {
      return { pcp1hIn: null, pcp3hIn: null, pcp6hIn: null, pcp24hIn: null, metarType: null };
    }
    let metarType: 'METAR' | 'SPECI' | null = null;
    if (/^\s*SPECI\b/i.test(raw)) metarType = 'SPECI';
    else if (/^\s*METAR\b/i.test(raw)) metarType = 'METAR';

    const rmkSplit = raw.split(/\bRMK\b/i);
    if (rmkSplit.length < 2) {
      return { pcp1hIn: null, pcp3hIn: null, pcp6hIn: null, pcp24hIn: null, metarType };
    }
    const rmk = rmkSplit[1].toUpperCase();
    const hasAutoGauge = /\bAO2\b/.test(rmk) && !/\bPNO\b/.test(rmk);

    let pcp1hIn: number | null = null;
    const m1 = rmk.match(/(?:^|\s)P(\d{4})(?=\s|$)/);
    if (m1) {
      pcp1hIn = Math.round(parseInt(m1[1], 10)) / 100.0;
    } else if (hasAutoGauge) {
      pcp1hIn = 0;
    }

    let pcp3hIn: number | null = null;
    let pcp6hIn: number | null = null;
    const m36 = rmk.match(/(?:^|\s)6(\d{4})(?=\s|$)/);
    if (m36) {
      const v36 = Math.round(parseInt(m36[1], 10)) / 100.0;
      const synHr = obsEpoch ? (((Math.round(obsEpoch / 3600) % 24) + 24) % 24) : 0;
      if (synHr === 0 || synHr === 6 || synHr === 12 || synHr === 18) {
        pcp6hIn = v36;
      } else {
        pcp3hIn = v36;
      }
    }

    let pcp24hIn: number | null = null;
    const m24 = rmk.match(/(?:^|\s)7(\d{4})(?=\s|$)/);
    if (m24) {
      pcp24hIn = Math.round(parseInt(m24[1], 10)) / 100.0;
    }

    return { pcp1hIn, pcp3hIn, pcp6hIn, pcp24hIn, metarType };
  }

  private static formatPrecipAmount(inches: number | null | undefined, isMetric: boolean): string {
    if (inches === null || inches === undefined || !Number.isFinite(inches)) return 'N/A';
    if (isMetric) {
      const mm = inches * 25.4;
      return mm > 0 && mm < 0.1 ? '<0.1 mm' : `${mm.toFixed(1)} mm`;
    }
    return inches > 0 && inches < 0.01 ? 'Trace' : `${inches.toFixed(2)} in`;
  }

  private computePrecipSeries(points: WxStationHistoryPoint[]): {
    hourlyPoints: Array<{ x: number; yIn: number; yMm: number }>;
    cumulativePoints: Array<{ x: number; yIn: number; yMm: number }>;
    totalIn: number;
    totalMm: number;
    last1hIn: number | null;
    hasGaugeData: boolean;
  } {
    // Group observations into 1-hour METAR buckets (shifted +600s so :50-:59 routine METARs close that hour)
    // to prevent double-counting intra-hour SPECI reports that repeat the running Pxxxx total.
    const bucketMap = new Map<number, { t: number; pcpIn: number | null; isRoutine: boolean }>();
    let hasAnyGauge = false;
    let maxSynoptic24hIn = 0;

    for (const p of points) {
      const parsedRmk = (p.pcp_in === undefined && p.raw)
        ? WxStationModal.parseClientMetarRemarksPrecip(p.raw, p.t)
        : null;
      const rawPcp = p.pcp_in !== undefined ? p.pcp_in : (parsedRmk ? parsedRmk.pcp1hIn : null);
      const pcpIn = (rawPcp !== null && rawPcp !== undefined && Number.isFinite(Number(rawPcp)) && Number(rawPcp) >= 0)
        ? Number(rawPcp)
        : null;

      const p24 = p.pcp24hr_in ?? parsedRmk?.pcp24hIn ?? null;
      if (p24 !== null && Number.isFinite(Number(p24)) && Number(p24) > maxSynoptic24hIn) {
        maxSynoptic24hIn = Number(p24);
        hasAnyGauge = true;
      }

      if (pcpIn !== null) {
        hasAnyGauge = true;
      }

      const mType = p.metar_type || parsedRmk?.metarType || (p.raw && /^\s*SPECI\b/i.test(p.raw) ? 'SPECI' : 'METAR');
      const isRoutine = mType !== 'SPECI';
      const bucketKey = Math.floor((p.t + 600) / 3600);
      const existing = bucketMap.get(bucketKey);

      if (!existing) {
        bucketMap.set(bucketKey, { t: p.t, pcpIn, isRoutine });
      } else {
        // Prefer a routine METAR or the latest report with the highest running hourly accumulation in that hour
        const nextPcp = pcpIn !== null && existing.pcpIn !== null
          ? Math.max(existing.pcpIn, pcpIn)
          : (pcpIn ?? existing.pcpIn);
        bucketMap.set(bucketKey, {
          t: isRoutine || !existing.isRoutine ? p.t : existing.t,
          pcpIn: nextPcp,
          isRoutine: existing.isRoutine || isRoutine
        });
      }
    }

    const sortedBuckets = Array.from(bucketMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([, v]) => v);

    const hourlyPoints: Array<{ x: number; yIn: number; yMm: number }> = [];
    const cumulativePoints: Array<{ x: number; yIn: number; yMm: number }> = [];
    let runningIn = 0;

    for (const b of sortedBuckets) {
      const incIn = b.pcpIn !== null ? Math.max(0, b.pcpIn) : 0;
      runningIn = Math.round((runningIn + incIn) * 100) / 100;
      const incMm = Math.round(incIn * 25.4 * 100) / 100;
      const cumMm = Math.round(runningIn * 25.4 * 100) / 100;
      hourlyPoints.push({ x: b.t, yIn: Math.round(incIn * 100) / 100, yMm: incMm });
      cumulativePoints.push({ x: b.t, yIn: runningIn, yMm: cumMm });
    }

    const totalIn = Math.round(Math.max(runningIn, points.length <= 36 ? maxSynoptic24hIn : runningIn) * 100) / 100;
    const totalMm = Math.round(totalIn * 25.4 * 10) / 10;
    const lastBucket = sortedBuckets.length > 0 ? sortedBuckets[sortedBuckets.length - 1] : null;

    return {
      hourlyPoints,
      cumulativePoints,
      totalIn,
      totalMm,
      last1hIn: lastBucket ? lastBucket.pcpIn : null,
      hasGaugeData: hasAnyGauge
    };
  }

  private resolveStationPrecipSummary(isMetric: boolean): { main: string; sub: string; hasRain: boolean } {
    const s = this.currentStation;
    if (!s) return { main: 'N/A', sub: 'Rain Gauge', hasRain: false };

    const rmkFallback = WxStationModal.parseClientMetarRemarksPrecip(s.raw);
    const p1hIn = s.pcp_in !== undefined && s.pcp_in !== null ? Number(s.pcp_in) : rmkFallback.pcp1hIn;
    const prop24hIn = s.pcp_24h_in !== undefined && s.pcp_24h_in !== null
      ? Number(s.pcp_24h_in)
      : (rmkFallback.pcp24hIn ?? s.pcp_6h_in ?? s.pcp_3h_in ?? null);

    let hist24hIn: number | null = null;
    let histWindowIn: number | null = null;
    let histLast1hIn: number | null = null;
    let histHasGauge = false;

    if (this.rawSeries.length > 0) {
      const nowSec = Math.floor(Date.now() / 1000);
      const p24 = this.computePrecipSeries(this.rawSeries.filter(p => p.t >= nowSec - 24 * 3600));
      if (p24.hasGaugeData) {
        hist24hIn = p24.totalIn;
        histLast1hIn = p24.last1hIn;
        histHasGauge = true;
      }
      if (this.activeWindowHours > 24) {
        const pWin = this.computePrecipSeries(this.rawSeries.filter(p => p.t >= nowSec - this.activeWindowHours * 3600));
        if (pWin.hasGaugeData) {
          histWindowIn = pWin.totalIn;
          histHasGauge = true;
        }
      }
    }

    const eff1hIn = p1hIn !== null && Number.isFinite(p1hIn) ? p1hIn : histLast1hIn;
    const eff24hIn = (hist24hIn !== null && prop24hIn !== null && Number.isFinite(Number(prop24hIn)))
      ? Math.max(hist24hIn, Number(prop24hIn))
      : (hist24hIn ?? (prop24hIn !== null && Number.isFinite(Number(prop24hIn)) ? Number(prop24hIn) : null));

    if (eff1hIn === null && eff24hIn === null && !histHasGauge) {
      const isPno = Boolean(s.raw && /\bPNO\b/i.test(s.raw));
      return {
        main: 'N/A',
        sub: isPno ? 'Sensor Offline (PNO)' : 'No Rain Gauge Report',
        hasRain: false
      };
    }

    const primaryIn = eff24hIn !== null ? eff24hIn : (eff1hIn ?? 0);
    const main = WxStationModal.formatPrecipAmount(primaryIn, isMetric);
    const subParts: string[] = [];

    if (eff24hIn !== null && eff1hIn !== null) {
      subParts.push(`1h: ${WxStationModal.formatPrecipAmount(eff1hIn, isMetric)}`);
      subParts.push(`24h: ${WxStationModal.formatPrecipAmount(eff24hIn, isMetric)}`);
    } else if (eff24hIn !== null) {
      subParts.push('24h Gauge Total');
    } else if (eff1hIn !== null) {
      subParts.push('Last 1h Gauge');
    }

    if (histWindowIn !== null && this.activeWindowHours > 24) {
      const winLbl = this.activeWindowHours === 168 ? '7d' : '14d';
      subParts.push(`${winLbl}: ${WxStationModal.formatPrecipAmount(histWindowIn, isMetric)}`);
    } else if (s.pcp_6h_in != null && Number(s.pcp_6h_in) > 0 && subParts.length < 2) {
      subParts.push(`6h: ${WxStationModal.formatPrecipAmount(Number(s.pcp_6h_in), isMetric)}`);
    }

    return {
      main,
      sub: subParts.join(' • ') || 'Rain Gauge (24h)',
      hasRain: primaryIn > 0 || (eff1hIn !== null && eff1hIn > 0)
    };
  }

  private updatePrecipCardDom(): void {
    const mainEl = this.dialog.querySelector('#wx-modal-precip-main') as HTMLElement | null;
    const subEl = this.dialog.querySelector('#wx-modal-precip-sub') as HTMLElement | null;
    if (!mainEl || !subEl) return;
    const isMetric = store.getState().units === 'metric';
    const summary = this.resolveStationPrecipSummary(isMetric);
    mainEl.textContent = summary.main;
    mainEl.style.color = summary.hasRain ? '#38bdf8' : '#e2e8f0';
    subEl.textContent = summary.sub;
  }

  private render(): void {
    if (!this.currentStation) return;
    const s = this.currentStation;
    const isMetric = store.getState().units === 'metric';

    const tempVal = isMetric
      ? (s.temp_c != null ? `${Number(s.temp_c).toFixed(1)}°C` : 'N/A')
      : (s.temp_f != null ? `${Number(s.temp_f).toFixed(1)}°F` : 'N/A');

    const feelsVal = isMetric
      ? (s.feels_c != null ? `Feels ${Number(s.feels_c).toFixed(1)}°C` : '')
      : (s.feels_f != null ? `Feels ${Number(s.feels_f).toFixed(1)}°F` : '');

    const dewVal = isMetric
      ? (s.dew_c != null ? `${Number(s.dew_c).toFixed(1)}°C` : 'N/A')
      : (s.dew_f != null ? `${Number(s.dew_f).toFixed(1)}°F` : 'N/A');

    const rhVal = s.rh != null ? `${Math.round(Number(s.rh))}%` : 'N/A';

    const cardinal = WxStationModal.degToCardinal(s.wdir);
    let windMain = 'Calm';
    let windSub = s.wdir != null ? `From ${cardinal} (${s.wdir}°)` : 'Variable / Calm';
    if (s.wspd_mph != null && Number(s.wspd_mph) > 0) {
      windMain = isMetric
        ? `${Math.round(Number(s.wspd_mph) * 1.60934)} km/h ${cardinal}`
        : `${Math.round(Number(s.wspd_mph))} mph ${cardinal}`;
      if (s.wgst_mph != null && Number(s.wgst_mph) > Number(s.wspd_mph)) {
        const gustStr = isMetric
          ? `${Math.round(Number(s.wgst_mph) * 1.60934)} km/h`
          : `${Math.round(Number(s.wgst_mph))} mph`;
        windSub = `Gusts ${gustStr}${s.wdir != null ? ` • ${s.wdir}°` : ''}`;
      }
    }

    const precipSummary = this.resolveStationPrecipSummary(isMetric);

    const pressMain = isMetric
      ? (s.slp_mb != null ? `${Number(s.slp_mb).toFixed(1)} mb` : (s.altim_inhg != null ? `${(Number(s.altim_inhg) * 33.86389).toFixed(1)} mb` : 'N/A'))
      : (s.altim_inhg != null ? `${Number(s.altim_inhg).toFixed(2)} inHg` : (s.slp_mb != null ? `${Number(s.slp_mb).toFixed(1)} mb` : 'N/A'));
    const pressSub = (!isMetric && s.slp_mb != null)
      ? `SLP ${Number(s.slp_mb).toFixed(1)} mb`
      : (isMetric && s.altim_inhg != null ? `Altim ${Number(s.altim_inhg).toFixed(2)}"` : 'Barometric');

    const hasSolarFallback = (
      s.vis_mi == null &&
      !s.wx &&
      !s.sky &&
      s.ceil_ft == null &&
      s.srad_wm2 != null
    );
    const fifthCardLabel = hasSolarFallback ? 'Solar Flux' : 'Visibility &amp; Sky';
    const visMain = hasSolarFallback
      ? `${Math.round(Number(s.srad_wm2))} W/m²`
      : (s.vis_mi != null
        ? (isMetric ? `${(Number(s.vis_mi) * 1.60934).toFixed(1)} km` : `${Number(s.vis_mi) >= 10 ? '10+' : Number(s.vis_mi)} mi`)
        : 'N/A');
    const skyParts: string[] = [];
    if (s.wx) skyParts.push(String(s.wx));
    if (s.sky) skyParts.push(String(s.sky));
    if (s.ceil_ft != null) {
      skyParts.push(isMetric ? `Ceil ${Math.round(Number(s.ceil_ft) * 0.3048)} m` : `Ceil ${Number(s.ceil_ft).toLocaleString()} ft`);
    }
    const visSub = hasSolarFallback
      ? 'Surface Insolation'
      : (skyParts.length > 0 ? skyParts.join(' • ') : 'Sky / Visibility');

    const elevStr = s.elev_ft != null
      ? (isMetric ? `${Math.round(Number(s.elev_ft) * 0.3048).toLocaleString()} m` : `${Number(s.elev_ft).toLocaleString()} ft`)
      : null;

    const flt = WxStationModal.flightCatStyle(s.flt_cat);
    const locParts = [s.state, s.country].filter(Boolean).join(', ');
    const coordStr = (s.lat != null && s.lon != null) ? `${Number(s.lat).toFixed(3)}°, ${Number(s.lon).toFixed(3)}°` : '';

    this.dialog.innerHTML = `
      <div class="wx-modal-scroll">
        <!-- Header Row -->
        <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;">
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
              <h2 id="wx-station-modal-title" style="margin: 0; font-size: 1.18rem; font-weight: 800; color: #f8fafc;">
                📡 ${WxStationModal.escapeHtml(s.name || s.id)}
              </h2>
              <span style="padding: 2px 8px; border-radius: 5px; font-size: 0.74rem; font-weight: 800; background: rgba(56, 189, 248, 0.16); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.35);">
                ${WxStationModal.escapeHtml(s.id)}
              </span>
              ${s.flt_cat ? `
                <span style="padding: 2px 8px; border-radius: 5px; font-size: 0.72rem; font-weight: 800; background: ${flt.bg}; color: ${flt.fg}; border: 1px solid ${flt.fg}44;">
                  ${WxStationModal.escapeHtml(flt.label)}
                </span>
              ` : ''}
            </div>
            <div style="font-size: 0.76rem; color: #94a3b8; display: flex; flex-wrap: wrap; gap: 8px;">
              ${locParts ? `<span>${WxStationModal.escapeHtml(locParts)}</span><span>•</span>` : ''}
              ${elevStr ? `<span>Elev ${elevStr}</span><span>•</span>` : ''}
              ${coordStr ? `<span>${coordStr}</span><span>•</span>` : ''}
              <span style="color: #cbd5e1;">Observed ${WxStationModal.formatObsTimestamp(s.obs_time)}</span>
            </div>
          </div>

          <form method="dialog" style="margin: 0;">
            <button type="submit" aria-label="Close weather station dialog" autofocus style="background: rgba(30, 41, 59, 0.9); border: 1px solid rgba(71, 85, 105, 0.6); color: #cbd5e1; width: 32px; height: 32px; border-radius: 8px; font-size: 18px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center;">
              &times;
            </button>
          </form>
        </div>

        <!-- Current Telemetry Cards -->
        <div class="wx-modal-metric-grid">
          <div class="wx-modal-metric-card">
            <span style="font-size: 0.68rem; font-weight: 700; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.4px;">Temperature</span>
            <span style="font-size: 1.25rem; font-weight: 800; color: #fb923c;">${tempVal}</span>
            <span style="font-size: 0.72rem; color: #94a3b8;">${feelsVal || '2m Air Temp'}</span>
          </div>
          <div class="wx-modal-metric-card">
            <span style="font-size: 0.68rem; font-weight: 700; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.4px;">Dew Point &amp; RH</span>
            <span style="font-size: 1.25rem; font-weight: 800; color: #38bdf8;">${dewVal}</span>
            <span style="font-size: 0.72rem; color: #67e8f9; font-weight: 600;">Humidity: ${rhVal}</span>
          </div>
          <div class="wx-modal-metric-card">
            <span style="font-size: 0.68rem; font-weight: 700; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.4px;">Surface Wind</span>
            <span style="font-size: 1.15rem; font-weight: 800; color: #4ade80;">${windMain}</span>
            <span style="font-size: 0.72rem; color: ${s.wgst_mph ? '#fb7185' : '#94a3b8'}; font-weight: ${s.wgst_mph ? '700' : '400'};">${windSub}</span>
          </div>
          <div class="wx-modal-metric-card">
            <span style="font-size: 0.68rem; font-weight: 700; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.4px;">Precipitation</span>
            <span id="wx-modal-precip-main" style="font-size: 1.15rem; font-weight: 800; color: ${precipSummary.hasRain ? '#38bdf8' : '#e2e8f0'};">${WxStationModal.escapeHtml(precipSummary.main)}</span>
            <span id="wx-modal-precip-sub" style="font-size: 0.72rem; color: #94a3b8;">${WxStationModal.escapeHtml(precipSummary.sub)}</span>
          </div>
          <div class="wx-modal-metric-card">
            <span style="font-size: 0.68rem; font-weight: 700; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.4px;">Pressure</span>
            <span style="font-size: 1.15rem; font-weight: 800; color: #c084fc;">${pressMain}</span>
            <span style="font-size: 0.72rem; color: #94a3b8;">${pressSub}</span>
          </div>
          <div class="wx-modal-metric-card">
            <span style="font-size: 0.68rem; font-weight: 700; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.4px;">${fifthCardLabel}</span>
            <span style="font-size: 1.15rem; font-weight: 800; color: #e2e8f0;">${visMain}</span>
            <span style="font-size: 0.72rem; color: #94a3b8;">${WxStationModal.escapeHtml(visSub)}</span>
          </div>
        </div>

        ${s.raw ? `
          <div style="background: rgba(2, 6, 23, 0.65); border: 1px solid rgba(51, 65, 85, 0.7); border-radius: 8px; padding: 8px 11px; display: flex; align-items: center; justify-content: space-between; gap: 10px;">
            <code style="font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.73rem; color: #cbd5e1; word-break: break-word; line-height: 1.35;">${WxStationModal.escapeHtml(s.raw)}</code>
            <button type="button" id="wx-copy-metar-btn" style="flex-shrink: 0; padding: 4px 8px; font-size: 0.68rem; font-weight: 700; border-radius: 5px; border: 1px solid rgba(71, 85, 105, 0.7); background: rgba(30, 41, 59, 0.85); color: #94a3b8; cursor: pointer;">Copy METAR</button>
          </div>
        ` : ''}

        <!-- Chart Controls Row -->
        <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; padding-top: 2px;">
          <div class="wx-modal-pill-group" role="tablist" aria-label="Observation parameter">
            <button type="button" class="wx-modal-pill-btn wx-metric-tab-btn ${this.activeChartTab === 'temp' ? 'active' : ''}" data-tab="temp">Temp, Dewpt &amp; RH</button>
            <button type="button" class="wx-modal-pill-btn wx-metric-tab-btn ${this.activeChartTab === 'precip' ? 'active' : ''}" data-tab="precip">Precip &amp; Accum</button>
            <button type="button" class="wx-modal-pill-btn wx-metric-tab-btn ${this.activeChartTab === 'wind' ? 'active' : ''}" data-tab="wind">Wind &amp; Gusts</button>
            <button type="button" class="wx-modal-pill-btn wx-metric-tab-btn ${this.activeChartTab === 'pressure' ? 'active' : ''}" data-tab="pressure">Pressure</button>
          </div>

          <div class="wx-modal-pill-group" role="tablist" aria-label="Historical timeframe">
            <button type="button" class="wx-modal-pill-btn wx-window-btn ${this.activeWindowHours === 24 ? 'active' : ''}" data-hours="24">Today (24H)</button>
            <button type="button" class="wx-modal-pill-btn wx-window-btn ${this.activeWindowHours === 168 ? 'active' : ''}" data-hours="168">7 Days</button>
            <button type="button" class="wx-modal-pill-btn wx-window-btn ${this.activeWindowHours === 336 ? 'active' : ''}" data-hours="336">14 Days</button>
          </div>
        </div>

        <!-- Status & Canvas -->
        <div style="background: rgba(2, 6, 23, 0.55); border: 1px solid rgba(51, 65, 85, 0.55); border-radius: 10px; padding: 12px;">
          <div id="wx-history-status" style="font-size: 0.74rem; color: #94a3b8; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap;">
            <span>${this.isLoading ? 'Loading live historical station observations...' : (this.loadError || '')}</span>
            <span id="wx-history-source-badge" style="font-size: 0.68rem; color: #64748b;"></span>
          </div>
          <div id="wx-chart-stage" style="position: relative; height: 294px; width: 100%;">
            <canvas id="wx-station-trend-canvas"></canvas>
            <button type="button" id="wx-forecast-expand-btn" class="wx-forecast-expand-btn" style="display: none;" aria-label="Extend chart with 24-hour NBM forecast">
              Forecast &rarr;
            </button>
            <button type="button" id="wx-forecast-collapse-btn" class="wx-forecast-collapse-btn" style="display: none;" aria-label="Hide 24-hour forecast">
              &times; Hide Forecast
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; font-size: 0.74rem; color: #64748b; padding-top: 2px;">
          <div style="display: flex; flex-direction: column; gap: 3px;">
            <span>${(!s.raw && s.id.length !== 4) ? 'Source: NOAA / NIFC Surface Weather Network' : 'Source: NOAA Aviation Weather Center (ASOS / AWOS / METAR)'}</span>
            <a href="${(!s.raw && s.id.length !== 4) ? `https://www.weather.gov/wrh/timeseries?site=${encodeURIComponent(s.id)}` : `https://aviationweather.gov/data/metar/?id=${encodeURIComponent(s.id)}&hours=24`}" target="_blank" rel="noopener noreferrer" style="color: #38bdf8; text-decoration: none; font-weight: 700;">
              Official NOAA Station Reports &rarr;
            </a>
          </div>
          ${(s.lat != null && s.lon != null) ? `
            <button type="button" id="wx-open-full-forecast-btn" class="wx-full-forecast-btn">
              📊 Full Detailed Forecast
            </button>
          ` : ''}
        </div>
      </div>
    `;

    this.bindEvents();
    if (!this.isLoading && this.rawSeries.length > 0) {
      this.renderChart();
    }
  }

  private bindEvents(): void {
    const copyBtn = this.dialog.querySelector('#wx-copy-metar-btn') as HTMLButtonElement | null;
    if (copyBtn && this.currentStation?.raw) {
      copyBtn.addEventListener('click', () => {
        navigator.clipboard?.writeText(this.currentStation?.raw || '').then(() => {
          copyBtn.textContent = '✓ Copied';
          setTimeout(() => { copyBtn.textContent = 'Copy METAR'; }, 1800);
        }).catch(() => { });
      });
    }

    const expandForecastBtn = this.dialog.querySelector('#wx-forecast-expand-btn') as HTMLButtonElement | null;
    if (expandForecastBtn) {
      expandForecastBtn.addEventListener('click', () => {
        void this.loadAndExpandForecast();
      });
    }

    const collapseForecastBtn = this.dialog.querySelector('#wx-forecast-collapse-btn') as HTMLButtonElement | null;
    if (collapseForecastBtn) {
      collapseForecastBtn.addEventListener('click', () => {
        this.forecastExpanded = false;
        this.renderChart();
      });
    }

    const fullForecastBtn = this.dialog.querySelector('#wx-open-full-forecast-btn') as HTMLButtonElement | null;
    if (fullForecastBtn && this.currentStation && this.currentStation.lat != null && this.currentStation.lon != null) {
      fullForecastBtn.addEventListener('click', () => {
        const s = this.currentStation;
        if (!s || s.lat == null || s.lon == null) return;
        const lat = Number(s.lat);
        const lon = Number(s.lon);
        const state = store.getState();
        const elevFt = (s.elev_ft != null && Number.isFinite(Number(s.elev_ft)))
          ? Number(s.elev_ft)
          : (state.selectedPoi?.elev ?? 0);
        const dateStr = PoiDataService.getTodayLocalDateStr(lat, lon);

        store.update({
          selectedPoi: {
            lat,
            lng: lon,
            elev: elevFt,
            name: s.name || s.id
          } as any
        });

        getElevationAt(lat, lon).then((profile) => {
          const cur = store.getState().selectedPoi;
          if (cur && cur.lat === lat && cur.lng === lon) {
            store.update({
              selectedPoi: {
                ...cur,
                elev: (s.elev_ft != null && Number.isFinite(Number(s.elev_ft))) ? Number(s.elev_ft) : profile.elevation,
                minElev: profile.minElevation,
                maxElev: profile.maxElevation
              }
            });
          }
        }).catch(() => { });

        this.close();
        void poiModal.open(lat, lon, elevFt, dateStr);
      });
    }

    this.dialog.querySelectorAll('.wx-metric-tab-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const tab = (target.getAttribute('data-tab') || 'temp') as HistoryChartTab;
        this.activeChartTab = tab;
        this.dialog.querySelectorAll('.wx-metric-tab-btn').forEach(b => b.classList.toggle('active', b === target));
        this.renderChart();
      });
    });

    this.dialog.querySelectorAll('.wx-window-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const hrs = parseInt(target.getAttribute('data-hours') || '24', 10) as HistoryHoursWindow;
        this.activeWindowHours = hrs;
        this.dialog.querySelectorAll('.wx-window-btn').forEach(b => b.classList.toggle('active', b === target));
        this.renderChart();
      });
    });
  }

  private async loadHistory(stationId: string): Promise<void> {
    const token = ++this.fetchToken;
    const cached = this.historyCache.get(stationId);
    if (cached && Date.now() - cached.fetchedAt < 5 * 60 * 1000) {
      this.rawSeries = cached.observations;
      this.activeSourceLabel = cached.source;
      this.isLoading = false;
      this.loadError = null;
      this.renderChart();
      return;
    }

    const state = store.getState();
    const apiServer = getApiServer(state.manifest);
    const historyPath = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.wx_stations?.history_api_path) || '/api/wx-station-history';
    const baseUrl = apiServer ? `${apiServer}${historyPath}` : historyPath;
    const proxyUrl = appendAuthToken(`${baseUrl}?id=${encodeURIComponent(stationId)}&hours=336`);

    try {
      const resp = await authenticatedFetch(proxyUrl);
      const denial = TierAccessService.parseTileAccessDenial(resp, 'registered');
      if (denial) {
        if (token !== this.fetchToken) return;
        this.accessDenial = denial;
        this.rawSeries = [];
        this.isLoading = false;
        this.loadError = null;
        this.renderChart();
        return;
      }
      if (resp.ok) {
        const contentType = resp.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const payload = await resp.json();
          if (token !== this.fetchToken) return;
          if (payload && Array.isArray(payload.observations) && payload.observations.length > 0) {
            this.accessDenial = null;
            this.rawSeries = payload.observations;
            this.activeSourceLabel = payload.source === 'IEM_HADS'
              ? 'NOAA HADS / IEM Archive'
              : (payload.source === 'NWS'
                ? 'NOAA / NWS Archive'
                : (payload.source === 'IEM' ? 'IEM ASOS Archive' : 'NOAA AWC Archive'));
            this.historyCache.set(stationId, {
              fetchedAt: Date.now(),
              source: this.activeSourceLabel,
              observations: this.rawSeries
            });
            this.isLoading = false;
            this.loadError = null;
            this.renderChart();
            return;
          }
        }
      }
    } catch (_) {
      // Fall through to direct CORS archives if local dev lacks Gatekeeper proxy
    }

    // Direct CORS fallback to IEM ASOS (4-char airport IDs) and NOAA/NWS Observations API (RAWS / NWSLI IDs)
    try {
      let fallbackObs: WxStationHistoryPoint[] = [];
      let fallbackLabel = 'IEM ASOS Archive';
      if (stationId.length === 4) {
        fallbackObs = await this.fetchDirectIemCorsHistory(stationId, 336);
      }
      if (fallbackObs.length === 0) {
        fallbackObs = await this.fetchDirectNwsCorsHistory(stationId, 336);
        if (fallbackObs.length > 0) {
          fallbackLabel = 'NOAA / NWS Archive';
        }
      }
      if (token !== this.fetchToken) return;
      this.rawSeries = fallbackObs;
      this.activeSourceLabel = fallbackLabel;
      if (fallbackObs.length > 0) {
        this.historyCache.set(stationId, {
          fetchedAt: Date.now(),
          source: this.activeSourceLabel,
          observations: fallbackObs
        });
      }
      this.isLoading = false;
      this.loadError = fallbackObs.length === 0 ? 'No historical observations reported for this station in the last 14 days.' : null;
      this.renderChart();
    } catch (err: any) {
      if (token !== this.fetchToken) return;
      this.isLoading = false;
      this.loadError = 'Unable to retrieve historical station observations from upstream archive.';
      this.updateStatusText(this.loadError, '');
    }
  }

  private async fetchDirectNwsCorsHistory(stationId: string, hours: number): Promise<WxStationHistoryPoint[]> {
    const cutoffSec = Math.floor((Date.now() - hours * 3600 * 1000) / 1000);
    const resp = await fetch(`https://api.weather.gov/stations/${encodeURIComponent(stationId)}/observations?limit=500`, {
      headers: { 'Accept': 'application/geo+json' }
    });
    if (!resp.ok) return [];
    const data: any = await resp.json();
    const feats = Array.isArray(data?.features) ? data.features : [];
    const byEpoch = new Map<number, WxStationHistoryPoint>();

    for (const f of feats) {
      const p = f?.properties;
      if (!p?.timestamp) continue;
      const ms = Date.parse(String(p.timestamp));
      if (!Number.isFinite(ms)) continue;
      const epoch = Math.floor(ms / 1000);
      if (epoch < cutoffSec) continue;

      const rawTc = typeof p.temperature?.value === 'number' ? p.temperature.value : null;
      const tempC = (rawTc !== null && Number.isFinite(rawTc)) ? Math.round(rawTc * 10) / 10 : null;
      const tempF = tempC !== null ? Math.round((tempC * 1.8 + 32) * 10) / 10 : null;

      const rawDc = typeof p.dewpoint?.value === 'number' ? p.dewpoint.value : null;
      let dewC = (rawDc !== null && Number.isFinite(rawDc)) ? Math.round(rawDc * 10) / 10 : null;
      const rawRh = typeof p.relativeHumidity?.value === 'number' ? p.relativeHumidity.value : null;
      const rh = (rawRh !== null && Number.isFinite(rawRh)) ? Math.max(0, Math.min(100, Math.round(rawRh))) : null;

      if (dewC === null && tempC !== null && rh !== null && rh > 0) {
        const gamma = Math.log(Math.max(1, rh) / 100) + (17.625 * tempC) / (243.04 + tempC);
        dewC = Math.round(Math.min(tempC, (243.04 * gamma) / (17.625 - gamma)) * 10) / 10;
      }
      const dewF = dewC !== null ? Math.round((dewC * 1.8 + 32) * 10) / 10 : null;

      const rawDir = typeof p.windDirection?.value === 'number' ? p.windDirection.value : null;
      const wdir = (rawDir !== null && Number.isFinite(rawDir)) ? ((Math.round(rawDir) % 360) + 360) % 360 : null;

      const toMph = (q: any): number | null => {
        const v = typeof q?.value === 'number' ? q.value : null;
        if (v === null || !Number.isFinite(v) || v < 0) return null;
        const u = String(q?.unitCode || '').toLowerCase();
        if (u.includes('m_s') || u.endsWith(':m/s')) return Math.round(v * 2.236936);
        if (u.includes('kt') || u.includes('kn')) return Math.round(v * 1.15078);
        if (u.includes('mi_h') || u.includes('mph')) return Math.round(v);
        return Math.round(v * 0.621371);
      };

      const wspdMph = toMph(p.windSpeed);
      const wgstMph = toMph(p.windGust);
      const wspdKt = wspdMph !== null ? Math.round(wspdMph / 1.15078) : null;

      const toPrecipIn = (q: any): number | null => {
        const v = typeof q?.value === 'number' ? q.value : null;
        if (v === null || !Number.isFinite(v) || v < 0) return null;
        const u = String(q?.unitCode || '').toLowerCase();
        let inches: number;
        if (u.includes(':mm') || u.endsWith('mm')) {
          inches = v / 25.4;
        } else if (u.includes(':m') || u.endsWith(':m')) {
          inches = (v * 1000.0) / 25.4;
        } else if (u.includes('in')) {
          inches = v;
        } else {
          inches = (v * 1000.0) / 25.4;
        }
        return inches <= 25.0 ? Math.round(inches * 100) / 100 : null;
      };

      const rawStr = p.rawMessage ? String(p.rawMessage).trim() : '';
      const rmkPcp = WxStationModal.parseClientMetarRemarksPrecip(rawStr, epoch);
      const pcp1h = toPrecipIn(p.precipitationLastHour) ?? rmkPcp.pcp1hIn;
      const pcp3h = toPrecipIn(p.precipitationLast3Hours) ?? rmkPcp.pcp3hIn;
      const pcp6h = toPrecipIn(p.precipitationLast6Hours) ?? rmkPcp.pcp6hIn;
      const pcp24h = rmkPcp.pcp24hIn;

      if (tempF === null && wspdMph === null && rh === null && pcp1h === null) continue;

      byEpoch.set(epoch, {
        t: epoch,
        temp_f: tempF,
        temp_c: tempC,
        dew_f: dewF,
        dew_c: dewC,
        rh,
        wdir,
        wspd_kt: wspdKt,
        wspd_mph: wspdMph,
        wgst_mph: wgstMph,
        altim_inhg: null,
        slp_mb: null,
        vis_mi: null,
        flt_cat: null,
        pcp_in: pcp1h,
        pcp_mm: pcp1h !== null ? Math.round(pcp1h * 25.4 * 100) / 100 : null,
        pcp3hr_in: pcp3h,
        pcp6hr_in: pcp6h,
        pcp24hr_in: pcp24h,
        metar_type: rmkPcp.metarType,
        raw: rawStr
      });
    }

    return Array.from(byEpoch.values()).sort((a, b) => a.t - b.t);
  }

  private async fetchDirectIemCorsHistory(stationId: string, hours: number): Promise<WxStationHistoryPoint[]> {
    const nowMs = Date.now();
    const cutoffSec = Math.floor((nowMs - hours * 3600 * 1000) / 1000);
    const startDt = new Date(nowMs - (hours + 24) * 3600 * 1000);
    const endDt = new Date(nowMs + 24 * 3600 * 1000);
    const candidates = (stationId.length === 4 && stationId.startsWith('K'))
      ? [stationId.slice(1), stationId]
      : [stationId];

    const parseNum = (v: string | undefined): number | null => {
      if (!v || v === 'null' || v === 'M') return null;
      const n = Number(v.trim());
      return Number.isFinite(n) ? n : null;
    };

    for (const code of candidates) {
      const params = new URLSearchParams();
      params.set('station', code);
      for (const col of ['tmpf', 'dwpf', 'relh', 'drct', 'sknt', 'gust', 'alti', 'mslp', 'vsby', 'p01i', 'metar']) {
        params.append('data', col);
      }
      params.set('year1', String(startDt.getUTCFullYear()));
      params.set('month1', String(startDt.getUTCMonth() + 1));
      params.set('day1', String(startDt.getUTCDate()));
      params.set('year2', String(endDt.getUTCFullYear()));
      params.set('month2', String(endDt.getUTCMonth() + 1));
      params.set('day2', String(endDt.getUTCDate()));
      params.set('tz', 'Etc/UTC');
      params.set('format', 'onlycomma');
      params.set('latlon', 'no');
      params.set('elev', 'no');
      params.set('missing', 'null');
      params.set('trace', '0.0001');
      params.set('direct', 'no');
      params.append('report_type', '3');
      params.append('report_type', '4');

      const resp = await fetch(`https://mesonet.agron.iastate.edu/cgi-bin/request/asos.py?${params.toString()}`);
      if (!resp.ok) continue;
      const csv = await resp.text();
      const lines = csv.split(/\r?\n/);
      const byEpoch = new Map<number, WxStationHistoryPoint>();

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line || line.startsWith('#') || line.startsWith('station,')) continue;
        const parts = line.split(',');
        if (parts.length < 12) continue;
        const valid = parts[1].trim();
        const ms = Date.parse(valid.includes('T') ? valid : `${valid.replace(' ', 'T')}:00Z`);
        if (!Number.isFinite(ms)) continue;
        const epoch = Math.floor(ms / 1000);
        if (epoch < cutoffSec) continue;

        const tempF = parseNum(parts[2]);
        const dewF = parseNum(parts[3]);
        const tempC = tempF !== null ? Math.round(((tempF - 32) / 1.8) * 10) / 10 : null;
        const dewC = dewF !== null ? Math.round(((dewF - 32) / 1.8) * 10) / 10 : null;
        const rhRaw = parseNum(parts[4]);
        const wdirRaw = parseNum(parts[5]);
        const sknt = parseNum(parts[6]);
        const gust = parseNum(parts[7]);
        const alti = parseNum(parts[8]);
        const mslp = parseNum(parts[9]);
        const vsby = parseNum(parts[10]);
        const p01iRaw = parseNum(parts[11]);
        const rawMetar = parts.length > 12 ? parts.slice(12).join(',').trim() : '';
        const rmkPcp = WxStationModal.parseClientMetarRemarksPrecip(rawMetar, epoch);
        const p01i = (p01iRaw !== null && p01iRaw >= 0 && p01iRaw <= 25)
          ? (p01iRaw > 0 && p01iRaw < 0.005 ? 0 : Math.round(p01iRaw * 100) / 100)
          : rmkPcp.pcp1hIn;

        byEpoch.set(epoch, {
          t: epoch,
          temp_f: tempF !== null ? Math.round(tempF * 10) / 10 : null,
          temp_c: tempC,
          dew_f: dewF !== null ? Math.round(dewF * 10) / 10 : null,
          dew_c: dewC,
          rh: rhRaw !== null ? Math.max(0, Math.min(100, Math.round(rhRaw))) : null,
          wdir: wdirRaw !== null ? Math.round(wdirRaw) % 360 : null,
          wspd_kt: sknt !== null ? Math.round(sknt) : null,
          wspd_mph: sknt !== null ? Math.round(sknt * 1.15078) : null,
          wgst_mph: gust !== null ? Math.round(gust * 1.15078) : null,
          altim_inhg: alti !== null ? Math.round(alti * 100) / 100 : null,
          slp_mb: mslp !== null ? Math.round(mslp * 10) / 10 : (alti !== null ? Math.round(alti * 33.86389 * 10) / 10 : null),
          vis_mi: vsby !== null ? Math.round(vsby * 100) / 100 : null,
          flt_cat: null,
          pcp_in: p01i,
          pcp_mm: p01i !== null ? Math.round(p01i * 25.4 * 100) / 100 : null,
          pcp3hr_in: rmkPcp.pcp3hIn,
          pcp6hr_in: rmkPcp.pcp6hIn,
          pcp24hr_in: rmkPcp.pcp24hIn,
          metar_type: rmkPcp.metarType,
          raw: rawMetar
        });
      }

      if (byEpoch.size > 0) {
        return Array.from(byEpoch.values()).sort((a, b) => a.t - b.t);
      }
    }
    return [];
  }

  private updateStatusText(msg: string, badge: string): void {
    const statusEl = this.dialog.querySelector('#wx-history-status');
    if (statusEl) {
      const spans = statusEl.querySelectorAll('span');
      if (spans[0]) spans[0].textContent = msg;
      if (spans[1]) spans[1].textContent = badge;
    }
  }

  private async loadAndExpandForecast(): Promise<void> {
    if (!this.currentStation || this.currentStation.lat == null || this.currentStation.lon == null) {
      return;
    }
    const lat = Number(this.currentStation.lat);
    const lon = Number(this.currentStation.lon);
    const state = store.getState();
    if (!state.manifest) return;

    const cacheKey = `${lat.toFixed(4)}_${lon.toFixed(4)}`;
    const cached = this.forecastCache.get(cacheKey);
    if (cached) {
      this.stationForecast = cached.forecast;
      this.forecastModelLabel = cached.label;
      this.forecastExpanded = true;
      this.isForecastLoading = false;
      this.renderChart();
      return;
    }

    const expandBtn = this.dialog.querySelector('#wx-forecast-expand-btn') as HTMLButtonElement | null;
    if (expandBtn) {
      expandBtn.disabled = true;
      expandBtn.textContent = 'Loading NBM...';
    }
    this.isForecastLoading = true;

    try {
      let fc = await ForecastBinaryService.getForecastForPoint(lat, lon, state.manifest, 'nbm');
      let modelLabel = 'NOAA NBM';
      if (!fc || fc.numHours === 0) {
        fc = await ForecastBinaryService.getForecastForPoint(lat, lon, state.manifest, 'blend');
        modelLabel = 'NBM / Blend';
      }
      this.isForecastLoading = false;
      if (fc && fc.numHours > 0) {
        this.stationForecast = fc;
        this.forecastModelLabel = modelLabel;
        this.forecastCache.set(cacheKey, { forecast: fc, label: modelLabel });
        this.forecastExpanded = true;
        this.renderChart();
      } else if (ForecastBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('forecast_point_tiles')) {
        if (expandBtn) {
          expandBtn.disabled = false;
          expandBtn.textContent = '🔒 Pro Required';
          setTimeout(() => {
            if (expandBtn) expandBtn.innerHTML = 'Forecast &rarr;';
          }, 2500);
        }
        TierAccessService.openTierUpgradeAction('pro');
      } else {
        if (expandBtn) {
          expandBtn.disabled = false;
          expandBtn.textContent = 'Unavailable';
          setTimeout(() => {
            if (expandBtn) expandBtn.innerHTML = 'Forecast &rarr;';
          }, 2000);
        }
      }
    } catch (_) {
      this.isForecastLoading = false;
      if (expandBtn) {
        expandBtn.disabled = false;
        expandBtn.innerHTML = 'Forecast &rarr;';
      }
    }
  }

  private sampleForecastSteps(lastObsEpoch: number, fc: DecodedPointForecast): SampledForecastStep[] {
    const steps: SampledForecastStep[] = [];
    if (!fc || fc.numHours <= 0) return steps;

    const interpAtFhr = (arr: number[] | undefined, fhr: number): number => {
      if (!arr || arr.length === 0) return 0;
      const clamped = Math.max(0, Math.min(arr.length - 1, fhr));
      const i0 = Math.floor(clamped);
      const i1 = Math.min(arr.length - 1, i0 + 1);
      const alpha = clamped - i0;
      const v0 = arr[i0] ?? 0;
      const v1 = arr[i1] ?? v0;
      return v0 + alpha * (v1 - v0);
    };

    const firstHourEpoch = (Math.floor(lastObsEpoch / 3600) + 1) * 3600;
    for (let h = 0; h < 24; h++) {
      const targetEpoch = firstHourEpoch + h * 3600;
      const fhr = (targetEpoch - fc.cycleEpoch) / 3600;
      if (fhr < -1 || fhr > fc.numHours + 1) continue;

      const tempC = Math.round(interpAtFhr(fc.tempsC, fhr) * 10) / 10;
      const tempF = Math.round((tempC * 1.8 + 32) * 10) / 10;
      const rh = Math.max(0, Math.min(100, Math.round(interpAtFhr(fc.relativeHumidity, fhr))));

      let dewC: number;
      let dewF: number;
      if (fc.dewpointC && fc.dewpointC.length > 0) {
        dewC = Math.round(interpAtFhr(fc.dewpointC, fhr) * 10) / 10;
        dewF = Math.round((dewC * 1.8 + 32) * 10) / 10;
      } else {
        const dp = ForecastBinaryService.calculateDewPoint(tempC, Math.max(1, rh));
        dewC = dp.dewpointC;
        dewF = dp.dewpointF;
      }

      const wspdMph = Math.max(0, Math.round(interpAtFhr(fc.windSpeedMph, fhr)));
      const rawGust = Math.round(interpAtFhr(fc.windGustMph, fhr));
      const wgstMph = rawGust > wspdMph ? rawGust : null;
      const pcpMm = Math.round(Math.max(0, interpAtFhr(fc.precipRateMmH, fhr)) * 100) / 100;
      const pcpIn = Math.round((pcpMm / 25.4) * 100) / 100;

      steps.push({
        t: targetEpoch,
        temp_c: tempC,
        temp_f: tempF,
        dew_c: dewC,
        dew_f: dewF,
        rh,
        wspd_mph: wspdMph,
        wgst_mph: wgstMph,
        pcp_in: pcpIn,
        pcp_mm: pcpMm
      });
    }

    return steps;
  }

  private renderChart(): void {
    this.destroyChart();
    this.updatePrecipCardDom();
    const canvas = this.dialog.querySelector('#wx-station-trend-canvas') as HTMLCanvasElement | null;
    const expandBtn = this.dialog.querySelector('#wx-forecast-expand-btn') as HTMLButtonElement | null;
    const collapseBtn = this.dialog.querySelector('#wx-forecast-collapse-btn') as HTMLButtonElement | null;
    if (expandBtn) expandBtn.style.display = 'none';
    if (collapseBtn) collapseBtn.style.display = 'none';
    if (!canvas) return;

    const stage = this.dialog.querySelector('#wx-chart-stage') as HTMLElement | null;
    const existingDenialEl = stage?.querySelector('#wx-chart-tier-denial') as HTMLElement | null;
    if (this.accessDenial) {
      canvas.style.display = 'none';
      this.updateStatusText('Account required for historical station trend charts.', '');
      let denialEl = existingDenialEl;
      if (!denialEl && stage) {
        denialEl = document.createElement('div');
        denialEl.id = 'wx-chart-tier-denial';
        denialEl.style.height = '100%';
        stage.appendChild(denialEl);
      }
      if (denialEl) {
        denialEl.innerHTML = TierAccessService.buildTrendChartDenialHtml({
          denial: this.accessDenial,
          requiredTier: 'registered',
          featureLabel: '14-day weather station observation trend charts'
        });
      }
      return;
    } else {
      if (existingDenialEl) existingDenialEl.remove();
      canvas.style.display = 'block';
    }

    const cutoffSec = Math.floor(Date.now() / 1000) - this.activeWindowHours * 3600;
    const filtered = this.rawSeries.filter(p => p.t >= cutoffSec);

    if (filtered.length === 0) {
      this.updateStatusText(
        this.loadError || 'No observations available in the selected timeframe.',
        this.activeSourceLabel
      );
      return;
    }

    const state = store.getState();
    const isMetric = state.units === 'metric';
    const lastObs = filtered[filtered.length - 1];
    const lastObsEpoch = lastObs.t;
    const firstObsEpoch = filtered[0].t;

    const canShowForecast =
      this.activeWindowHours === 24 &&
      (this.activeChartTab === 'temp' || this.activeChartTab === 'wind' || this.activeChartTab === 'precip') &&
      this.currentStation?.lat != null &&
      this.currentStation?.lon != null &&
      Boolean(state.manifest?.forecast_point_tiles?.available);

    const forecastSteps: SampledForecastStep[] =
      (canShowForecast && this.forecastExpanded && this.stationForecast)
        ? this.sampleForecastSteps(lastObsEpoch, this.stationForecast)
        : [];

    const isForecastActive = canShowForecast && this.forecastExpanded && forecastSteps.length > 0;
    const isPreviewStripActive = canShowForecast && !isForecastActive;
    const previewEndEpoch = lastObsEpoch + 3 * 3600;

    const windowLabel = this.activeWindowHours === 24 ? 'Past 24 Hours' : (this.activeWindowHours === 168 ? 'Past 7 Days' : 'Past 14 Days');
    if (isForecastActive) {
      this.updateStatusText(
        `${windowLabel} (${filtered.length} METAR reports) + 24H ${this.forecastModelLabel} Forecast`,
        `${this.activeSourceLabel} • ${this.forecastModelLabel}`
      );
    } else {
      this.updateStatusText(`${windowLabel} (${filtered.length} METAR reports)`, this.activeSourceLabel);
    }

    const datasets: any[] = [];
    let yTitle = '';
    let yMin: number | undefined;
    let yMax: number | undefined;
    const isDualAxisTempRh = this.activeChartTab === 'temp';
    const isDualAxisPrecip = this.activeChartTab === 'precip';
    let precipY1Max: number | undefined;

    if (this.activeChartTab === 'temp') {
      yTitle = isMetric ? 'Temperature (°C)' : 'Temperature (°F)';

      const tempPoints: Array<{ x: number; y: number | null }> = filtered.map(p => ({
        x: p.t,
        y: isMetric ? p.temp_c : p.temp_f
      }));
      const dewPoints: Array<{ x: number; y: number | null }> = filtered.map(p => ({
        x: p.t,
        y: isMetric ? p.dew_c : p.dew_f
      }));
      const rhPoints: Array<{ x: number; y: number | null }> = filtered.map(p => ({
        x: p.t,
        y: p.rh
      }));

      if (isPreviewStripActive) {
        tempPoints.push({ x: previewEndEpoch, y: isMetric ? lastObs.temp_c : lastObs.temp_f });
        dewPoints.push({ x: previewEndEpoch, y: isMetric ? lastObs.dew_c : lastObs.dew_f });
        rhPoints.push({ x: previewEndEpoch, y: lastObs.rh });
      } else if (isForecastActive) {
        for (const step of forecastSteps) {
          tempPoints.push({ x: step.t, y: isMetric ? step.temp_c : step.temp_f });
          dewPoints.push({ x: step.t, y: isMetric ? step.dew_c : step.dew_f });
          rhPoints.push({ x: step.t, y: step.rh });
        }
      }

      datasets.push({
        label: isMetric ? 'Air Temp (°C)' : 'Air Temp (°F)',
        data: tempPoints,
        yAxisID: 'y',
        borderColor: '#fb923c',
        backgroundColor: 'rgba(251, 146, 60, 0.14)',
        borderWidth: 2.2,
        pointRadius: (ctx: any) => {
          const pt = ctx.raw as { x: number } | undefined;
          if (isPreviewStripActive && pt && pt.x > lastObsEpoch) return 0;
          return this.activeWindowHours <= 24 ? 1.8 : 0;
        },
        pointHoverRadius: 4,
        tension: 0.25,
        spanGaps: true,
        fill: false
      });

      datasets.push({
        label: isMetric ? 'Dew Point (°C)' : 'Dew Point (°F)',
        data: dewPoints,
        yAxisID: 'y',
        borderColor: '#38bdf8',
        backgroundColor: 'rgba(56, 189, 248, 0.1)',
        borderWidth: 1.8,
        borderDash: [4, 3],
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.25,
        spanGaps: true,
        fill: false
      });

      datasets.push({
        label: 'Humidity (%)',
        data: rhPoints,
        yAxisID: 'y1',
        borderColor: 'rgba(34, 211, 238, 0.78)',
        backgroundColor: 'rgba(34, 211, 238, 0.08)',
        borderWidth: 1.6,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.25,
        spanGaps: true,
        fill: true
      });
    } else if (this.activeChartTab === 'wind') {
      yTitle = isMetric ? 'Wind Speed (km/h)' : 'Wind Speed (mph)';
      yMin = 0;

      const windPoints: Array<{ x: number; y: number | null }> = filtered.map(p => ({
        x: p.t,
        y: p.wspd_mph != null ? (isMetric ? Math.round(p.wspd_mph * 1.60934) : p.wspd_mph) : null
      }));
      const gustPoints: Array<{ x: number; y: number | null }> = filtered.map(p => ({
        x: p.t,
        y: p.wgst_mph != null ? (isMetric ? Math.round(p.wgst_mph * 1.60934) : p.wgst_mph) : null
      }));

      if (isPreviewStripActive) {
        windPoints.push({
          x: previewEndEpoch,
          y: lastObs.wspd_mph != null ? (isMetric ? Math.round(lastObs.wspd_mph * 1.60934) : lastObs.wspd_mph) : null
        });
        gustPoints.push({ x: previewEndEpoch, y: null });
      } else if (isForecastActive) {
        for (const step of forecastSteps) {
          windPoints.push({
            x: step.t,
            y: isMetric ? Math.round(step.wspd_mph * 1.60934) : step.wspd_mph
          });
          gustPoints.push({
            x: step.t,
            y: step.wgst_mph != null ? (isMetric ? Math.round(step.wgst_mph * 1.60934) : step.wgst_mph) : null
          });
        }
      }

      datasets.push({
        label: isMetric ? 'Sustained Wind (km/h)' : 'Sustained Wind (mph)',
        data: windPoints,
        yAxisID: 'y',
        borderColor: '#4ade80',
        backgroundColor: 'rgba(74, 222, 128, 0.14)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.2,
        spanGaps: true,
        fill: true
      });
      datasets.push({
        label: isMetric ? 'Wind Gusts (km/h)' : 'Wind Gusts (mph)',
        data: gustPoints,
        yAxisID: 'y',
        borderColor: '#fb7185',
        backgroundColor: '#fb7185',
        borderWidth: 1.5,
        showLine: false,
        pointRadius: 3.2,
        pointHoverRadius: 5
      });
    } else if (this.activeChartTab === 'precip') {
      yTitle = isMetric ? 'Hourly Precip (mm)' : 'Hourly Precip (in)';
      yMin = 0;

      const pcpSummary = this.computePrecipSeries(filtered);
      const hourlyPoints: Array<{ x: number; y: number | null }> = pcpSummary.hourlyPoints.map((pt: { x: number; yIn: number; yMm: number }) => ({
        x: pt.x,
        y: isMetric ? pt.yMm : pt.yIn
      }));
      const cumPoints: Array<{ x: number; y: number | null }> = pcpSummary.cumulativePoints.map((pt: { x: number; yIn: number; yMm: number }) => ({
        x: pt.x,
        y: isMetric ? pt.yMm : pt.yIn
      }));

      let runningCumIn = pcpSummary.totalIn;
      let runningCumMm = pcpSummary.totalMm;

      if (isPreviewStripActive) {
        const lastCum = cumPoints.length > 0 ? cumPoints[cumPoints.length - 1].y : 0;
        cumPoints.push({ x: previewEndEpoch, y: lastCum });
      } else if (isForecastActive) {
        for (const step of forecastSteps) {
          runningCumIn = Math.round((runningCumIn + step.pcp_in) * 100) / 100;
          runningCumMm = Math.round((runningCumMm + step.pcp_mm) * 100) / 100;
          hourlyPoints.push({
            x: step.t,
            y: isMetric ? step.pcp_mm : step.pcp_in
          });
          cumPoints.push({
            x: step.t,
            y: isMetric ? runningCumMm : runningCumIn
          });
        }
      }

      const maxHourlyVal = hourlyPoints.reduce((acc, p) => Math.max(acc, p.y ?? 0), 0);
      const maxCumVal = cumPoints.reduce((acc, p) => Math.max(acc, p.y ?? 0), 0);
      yMax = isMetric ? Math.max(2.0, Math.ceil(maxHourlyVal * 1.25 * 10) / 10) : Math.max(0.1, Math.ceil(maxHourlyVal * 1.25 * 20) / 20);
      precipY1Max = isMetric ? Math.max(5.0, Math.ceil(maxCumVal * 1.15 * 10) / 10) : Math.max(0.25, Math.ceil(maxCumVal * 1.15 * 20) / 20);

      datasets.push({
        type: 'bar',
        label: isMetric ? 'Hourly Precip (mm)' : 'Hourly Precip (in)',
        data: hourlyPoints,
        yAxisID: 'y',
        backgroundColor: 'rgba(56, 189, 248, 0.55)',
        borderColor: '#38bdf8',
        borderWidth: 1,
        barThickness: this.activeWindowHours <= 24 ? 8 : (this.activeWindowHours <= 168 ? 4 : 2.5),
        maxBarThickness: 12,
        order: 2
      });

      datasets.push({
        type: 'line',
        label: isMetric ? 'Cumulative Total (mm)' : 'Cumulative Total (in)',
        data: cumPoints,
        yAxisID: 'y1',
        borderColor: '#34d399',
        backgroundColor: 'rgba(52, 211, 153, 0.10)',
        borderWidth: 2.2,
        pointRadius: (ctx: any) => {
          const pt = ctx.raw as { x: number } | undefined;
          if (isPreviewStripActive && pt && pt.x > lastObsEpoch) return 0;
          return this.activeWindowHours <= 24 ? 1.8 : 0;
        },
        pointHoverRadius: 4,
        tension: 0.2,
        spanGaps: true,
        fill: true,
        order: 1
      });
    } else {
      yTitle = isMetric ? 'Sea-Level Pressure (mb)' : 'Altimeter (inHg)';
      datasets.push({
        label: yTitle,
        data: filtered.map(p => ({
          x: p.t,
          y: isMetric ? p.slp_mb : p.altim_inhg
        })),
        yAxisID: 'y',
        borderColor: '#c084fc',
        backgroundColor: 'rgba(192, 132, 252, 0.14)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.25,
        spanGaps: true,
        fill: true
      });
    }

    const xMaxEpoch = isPreviewStripActive
      ? previewEndEpoch
      : (isForecastActive
        ? forecastSteps[forecastSteps.length - 1].t
        : lastObsEpoch);

    const forecastZonePlugin = {
      id: 'wxForecastZonePlugin',
      beforeLayout: (chartInstance: any) => {
        if (!isPreviewStripActive && !isForecastActive) return;
        const legend = chartInstance.legend;
        if (legend && !legend._wxBufferPatched && typeof legend.fit === 'function') {
          legend._wxBufferPatched = true;
          const origFit = legend.fit;
          legend.fit = function () {
            origFit.call(this);
            this.height += 24;
          };
        }
      },
      afterDatasetsDraw: (chartInstance: Chart) => {
        if (!isPreviewStripActive && !isForecastActive) return;
        const xScale = chartInstance.scales['x'];
        const area = chartInstance.chartArea;
        if (!xScale || !area) return;

        const rawDividerX = xScale.getPixelForValue(lastObsEpoch);
        const xDivider = Math.max(area.left, Math.min(area.right, rawDividerX));
        const ctx = chartInstance.ctx;

        ctx.save();

        if (isPreviewStripActive && xDivider < area.right) {
          // Gradient fade-out over the ~3-hour right-hand forecast preview strip
          const fadeGrad = ctx.createLinearGradient(xDivider, 0, area.right, 0);
          fadeGrad.addColorStop(0, 'rgba(15, 23, 42, 0.25)');
          fadeGrad.addColorStop(0.35, 'rgba(9, 15, 29, 0.72)');
          fadeGrad.addColorStop(1, 'rgba(2, 6, 23, 0.95)');
          ctx.fillStyle = fadeGrad;
          ctx.fillRect(xDivider, area.top, area.right - xDivider, area.bottom - area.top);

          // Subtle sky-blue ambient glow near the transition boundary
          const glowGrad = ctx.createLinearGradient(xDivider, 0, area.right, 0);
          glowGrad.addColorStop(0, 'rgba(56, 189, 248, 0.14)');
          glowGrad.addColorStop(1, 'rgba(56, 189, 248, 0.02)');
          ctx.fillStyle = glowGrad;
          ctx.fillRect(xDivider, area.top, area.right - xDivider, area.bottom - area.top);

          // Dotted vertical line marking where realtime observations end
          ctx.beginPath();
          ctx.setLineDash([4, 4]);
          ctx.strokeStyle = 'rgba(56, 189, 248, 0.75)';
          ctx.lineWidth = 1.6;
          ctx.moveTo(xDivider, area.top);
          ctx.lineTo(xDivider, area.bottom);
          ctx.stroke();

          // "<- Observed" header label above the marker
          ctx.setLineDash([]);
          ctx.font = '700 10.5px system-ui, -apple-system, sans-serif';
          ctx.fillStyle = '#cbd5e1';
          ctx.textAlign = 'right';
          ctx.textBaseline = 'bottom';
          ctx.fillText('← Observed', Math.max(area.left + 64, xDivider - 7), area.top - 4);

          if (expandBtn) {
            const centerX = xDivider + (area.right - xDivider) / 2;
            const centerY = area.top + (area.bottom - area.top) / 2;
            expandBtn.style.display = 'inline-flex';
            expandBtn.style.left = `${Math.round(centerX)}px`;
            expandBtn.style.top = `${Math.round(centerY)}px`;
            if (!this.isForecastLoading) {
              expandBtn.disabled = false;
              expandBtn.innerHTML = 'Forecast &rarr;';
            }
          }
        } else if (isForecastActive) {
          // Subtle forecast zone background tint
          if (xDivider < area.right) {
            ctx.fillStyle = 'rgba(56, 189, 248, 0.045)';
            ctx.fillRect(xDivider, area.top, area.right - xDivider, area.bottom - area.top);
          }

          // Dotted vertical line marking where realtime observations end
          ctx.beginPath();
          ctx.setLineDash([4, 4]);
          ctx.strokeStyle = '#38bdf8';
          ctx.lineWidth = 1.8;
          ctx.moveTo(xDivider, area.top);
          ctx.lineTo(xDivider, area.bottom);
          ctx.stroke();

          // Top indicator line & "<- Observed | Forecast ->" labels in the buffer band above the chart area
          ctx.setLineDash([]);
          const bannerY = area.top - 4;
          const spanPx = Math.min(92, Math.min(xDivider - area.left - 6, area.right - xDivider - 6));

          if (spanPx > 28) {
            ctx.beginPath();
            ctx.strokeStyle = 'rgba(148, 163, 184, 0.42)';
            ctx.lineWidth = 1.2;
            ctx.moveTo(xDivider - spanPx, bannerY - 13);
            ctx.lineTo(xDivider + spanPx, bannerY - 13);
            ctx.stroke();
          }

          ctx.beginPath();
          ctx.strokeStyle = '#38bdf8';
          ctx.lineWidth = 1.6;
          ctx.moveTo(xDivider, area.top - 17);
          ctx.lineTo(xDivider, area.top);
          ctx.stroke();

          ctx.font = '700 11px system-ui, -apple-system, sans-serif';
          ctx.textBaseline = 'bottom';

          ctx.fillStyle = '#e2e8f0';
          ctx.textAlign = 'right';
          ctx.fillText('← Observed', xDivider - 7, bannerY);

          ctx.fillStyle = '#38bdf8';
          ctx.textAlign = 'left';
          ctx.fillText('Forecast →', xDivider + 7, bannerY);

          if (collapseBtn) {
            collapseBtn.style.display = 'inline-flex';
            collapseBtn.style.top = `${Math.round(area.top + 6)}px`;
            collapseBtn.style.right = `${Math.round(chartInstance.width - area.right + 8)}px`;
          }
        }

        ctx.restore();
      }
    };

    const scalesConfig: any = {
      x: {
        type: 'linear',
        min: firstObsEpoch,
        max: xMaxEpoch,
        ticks: {
          color: '#94a3b8',
          maxTicksLimit: this.activeWindowHours <= 24 ? 8 : 7,
          maxRotation: 0,
          font: { size: 10.5 },
          callback: (val: any) => {
            const sec = Number(val);
            if (!Number.isFinite(sec)) return '';
            const d = new Date(sec * 1000);
            if (this.activeWindowHours <= 24) {
              return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
            }
            return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: 'numeric' });
          }
        },
        grid: { color: 'rgba(148, 163, 184, 0.08)' }
      },
      y: {
        position: 'left',
        min: yMin,
        max: yMax,
        title: {
          display: true,
          text: yTitle,
          color: '#94a3b8',
          font: { size: 11, weight: 'bold' }
        },
        ticks: {
          color: '#cbd5e1',
          font: { size: 10.5 }
        },
        grid: { color: 'rgba(148, 163, 184, 0.1)' }
      }
    };

    if (isDualAxisTempRh) {
      scalesConfig.y1 = {
        position: 'right',
        min: 0,
        max: 100,
        title: {
          display: true,
          text: 'Humidity (%)',
          color: '#22d3ee',
          font: { size: 11, weight: 'bold' }
        },
        ticks: {
          color: '#67e8f9',
          font: { size: 10.5 },
          stepSize: 25,
          callback: (val: any) => `${val}%`
        },
        grid: {
          drawOnChartArea: false
        }
      };
    } else if (isDualAxisPrecip) {
      scalesConfig.y1 = {
        position: 'right',
        min: 0,
        max: precipY1Max,
        title: {
          display: true,
          text: isMetric ? 'Cumulative Total (mm)' : 'Cumulative Total (in)',
          color: '#34d399',
          font: { size: 11, weight: 'bold' }
        },
        ticks: {
          color: '#6ee7b7',
          font: { size: 10.5 },
          callback: (val: any) => {
            const n = Number(val);
            if (!Number.isFinite(n)) return '';
            return isMetric ? `${n.toFixed(1)}` : `${n.toFixed(2)}"`;
          }
        },
        grid: {
          drawOnChartArea: false
        }
      };
    }

    this.chart = new Chart(canvas, {
      type: 'line',
      data: { datasets },
      plugins: [forecastZonePlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 4
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              color: '#cbd5e1',
              boxWidth: 12,
              font: { size: 11, weight: 'bold' }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f8fafc',
            bodyColor: '#e2e8f0',
            borderColor: 'rgba(56, 189, 248, 0.4)',
            borderWidth: 1,
            filter: (item: any) => {
              if (isPreviewStripActive && item?.parsed?.x > lastObsEpoch) {
                return false;
              }
              return true;
            },
            callbacks: {
              title: (items: any[]) => {
                if (!items || items.length === 0) return '';
                const sec = Number(items[0]?.parsed?.x);
                if (!Number.isFinite(sec)) return '';
                const d = new Date(sec * 1000);
                const timeStr = d.toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                });
                if (isForecastActive && sec > lastObsEpoch) {
                  return `${timeStr} (${this.forecastModelLabel} Forecast)`;
                }
                return `${timeStr} (Observed)`;
              }
            }
          }
        },
        scales: scalesConfig
      }
    });
  }
}

export const wxStationModal = new WxStationModal();
export default WxStationModal;
